import { useEffect, useState } from "react";
import { api, fmtMoney, fmtDateTime } from "@/lib/api";
import { Card } from "@/components/ds/Card";
import { PageShell, PageHeader, Section, Loading } from "@/components/ds/PageShell";
import { Stat } from "@/components/ds/Stat";
import { SeverityDot } from "@/components/ds/Severity";

const TABS = [
    { key: "daily", label: "Daily" },
    { key: "weekly", label: "Weekly" },
    { key: "monthly", label: "Monthly" },
];

export default function Reports() {
    const [active, setActive] = useState("daily");
    const [daily, setDaily] = useState(null);
    const [weekly, setWeekly] = useState(null);
    const [monthly, setMonthly] = useState(null);

    useEffect(() => {
        api.get("/reports/daily").then((r) => setDaily(r.data));
        api.get("/reports/weekly").then((r) => setWeekly(r.data));
        api.get("/reports/monthly").then((r) => setMonthly(r.data));
    }, []);

    return (
        <PageShell width="default" testId="reports-page">
            <PageHeader
                eyebrow="Executive briefings"
                title="Reports"
                subtitle="Daily, weekly and monthly management briefings derived from the deterministic data layer. Send the monthly view to your accountant."
            />

            <div className="inline-flex items-center gap-1 mb-5 p-1 rounded-full bg-stone-100">
                {TABS.map((t) => (
                    <button
                        key={t.key}
                        type="button"
                        onClick={() => setActive(t.key)}
                        data-testid={`tab-${t.key}`}
                        className={`text-[13px] px-4 py-1.5 rounded-full font-medium transition ${
                            active === t.key
                                ? "bg-white text-stone-900 shadow-sm"
                                : "text-stone-600 hover:text-stone-800"
                        }`}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            {active === "daily" && (
                <DailyView daily={daily} />
            )}
            {active === "weekly" && (
                <WeeklyView weekly={weekly} />
            )}
            {active === "monthly" && (
                <MonthlyView monthly={monthly} />
            )}
        </PageShell>
    );
}

function DailyView({ daily }) {
    if (!daily) return <Loading label="Loading daily briefing…" />;
    return (
        <>
            <Card variant="default" padding="lg" className="mb-6">
                <div className="label-soft mb-2 text-stone-500">Generated {fmtDateTime(daily.generated_at)}</div>
                <div className="text-display-md text-stone-900 leading-snug mb-1">
                    Daily group snapshot
                </div>
                <p className="text-body text-stone-500 max-w-prose">
                    Today's deterministic numbers across the six entities.
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-8 gap-y-5 mt-6">
                    <Stat label="Total cash" value={daily.group_summary.total_cash} size="sm" />
                    <Stat label="Revenue MTD" value={daily.group_summary.total_revenue_mtd} size="sm" />
                    <Stat label="Net profit MTD" value={daily.group_summary.total_net_profit_mtd} size="sm" tone={daily.group_summary.total_net_profit_mtd >= 0 ? "positive" : "negative"} />
                    <Stat label="Receivables" value={daily.group_summary.total_ar} size="sm" />
                    <Stat label="Payables" value={daily.group_summary.total_ap} size="sm" />
                    <Stat label="Risk" value={`${daily.group_summary.risk_score}/100`} money={false} size="sm" />
                </div>
            </Card>

            <Section title="Urgent today" subtitle="Critical and high-severity findings.">
                <Card variant="default" padding="lg">
                    {daily.urgent_findings.length === 0 ? (
                        <div className="text-[14px] text-stone-500">All clear.</div>
                    ) : (
                        <ul className="space-y-3">
                            {daily.urgent_findings.map((f) => (
                                <li key={f.id} className="flex items-start gap-3">
                                    <SeverityDot level={f.severity} className="mt-1.5" />
                                    <div className="text-[14px] text-stone-800">{f.title}</div>
                                </li>
                            ))}
                        </ul>
                    )}
                </Card>
            </Section>
        </>
    );
}

function WeeklyView({ weekly }) {
    if (!weekly) return <Loading label="Loading weekly briefing…" />;
    return (
        <Section title="Entity-by-entity" subtitle="Week-to-date performance.">
            <Card variant="default" padding="none" className="overflow-hidden">
                <table className="w-full text-[14px]">
                    <thead>
                        <tr className="text-left border-b border-stone-100 bg-stone-50/40">
                            <th className="px-5 py-3 label-soft text-stone-500 font-medium">Entity</th>
                            <th className="px-3 py-3 label-soft text-stone-500 font-medium text-right">Revenue MTD</th>
                            <th className="px-3 py-3 label-soft text-stone-500 font-medium text-right">MoM</th>
                            <th className="px-3 py-3 label-soft text-stone-500 font-medium text-right">NP MTD</th>
                            <th className="px-5 py-3 label-soft text-stone-500 font-medium text-right">Cash</th>
                        </tr>
                    </thead>
                    <tbody>
                        {weekly.per_entity.map((p) => (
                            <tr key={p.entity_id} className="border-b border-stone-100 last:border-0">
                                <td className="px-5 py-3.5 text-stone-800">{p.display_name}</td>
                                <td className="px-3 py-3.5 font-mono-num text-right text-stone-700">{fmtMoney(p.revenue_mtd)}</td>
                                <td className={`px-3 py-3.5 font-mono-num text-right ${p.revenue_mom_change_pct >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                                    {p.revenue_mom_change_pct >= 0 ? "+" : ""}{p.revenue_mom_change_pct.toFixed(1)}%
                                </td>
                                <td className={`px-3 py-3.5 font-mono-num text-right ${p.net_profit_mtd >= 0 ? "text-stone-700" : "text-red-700"}`}>
                                    {fmtMoney(p.net_profit_mtd)}
                                </td>
                                <td className="px-5 py-3.5 font-mono-num text-right text-stone-700">{fmtMoney(p.cash_position)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </Card>
        </Section>
    );
}

function MonthlyView({ monthly }) {
    if (!monthly) return <Loading label="Loading monthly briefing…" />;
    return (
        <>
            <Card variant="default" padding="lg" className="mb-6">
                <div className="label-soft mb-2 text-stone-500">Management summary · MTD</div>
                <div className="text-display-md text-stone-900 leading-snug mb-1">
                    Consolidated group view
                </div>
                <p className="text-body text-stone-500 max-w-prose">
                    Accountant-ready snapshot for the month so far.
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-8 gap-y-5 mt-6">
                    <Stat label="Total cash" value={monthly.group.total_cash} size="sm" />
                    <Stat label="Revenue MTD" value={monthly.group.total_revenue_mtd} size="sm" />
                    <Stat label="Net profit MTD" value={monthly.group.total_net_profit_mtd} size="sm" tone={monthly.group.total_net_profit_mtd >= 0 ? "positive" : "negative"} />
                    <Stat label="Risk score" value={`${monthly.group.risk_score}/100`} money={false} size="sm" />
                </div>
            </Card>

            <Section
                title={`All findings (${monthly.all_findings.length})`}
                subtitle="Full audit detail for the accountant."
            >
                <Card variant="default" padding="lg">
                    <ul className="space-y-3">
                        {monthly.all_findings.slice(0, 30).map((f) => (
                            <li key={f.id} className="flex items-start gap-3">
                                <SeverityDot level={f.severity} className="mt-1.5" />
                                <div className="flex-1 min-w-0">
                                    <div className="text-[14px] font-medium text-stone-900 leading-snug">
                                        {f.title}
                                    </div>
                                    <div className="text-[13px] text-stone-500 mt-1">
                                        {f.category} · {f.recommended_action}
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                </Card>
            </Section>
        </>
    );
}
